import { Text, TouchableOpacity, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import DropdownMenu from '@/components/General/DropdownMenu';
import { formVisibilityLabel } from '@/utils/formPermissions';
import GuideTarget from '@/components/Guides/GuideTarget';

const STATUS_CONFIG = {
    draft: { label: 'Draft', tone: 'textSecondary' },
    published: { label: 'Published', tone: 'success' },
    closed: { label: 'Closed', tone: 'warning' },
    archived: { label: 'Archived', tone: 'textSecondary' },
};

export default function FormCard({ form, onPress, actionLabel, onAction, menuOptions = [], showEntryCount = false, subtitle, myEntry = null, onMyEntryPress, guideTargets = {}, guideScrollRef }) {
    const status = STATUS_CONFIG[form.status] || STATUS_CONFIG.draft;
    const anchor = (id, children) => id ? <GuideTarget id={id} scrollRef={guideScrollRef}>{children}</GuideTarget> : children;
    return (
        <View style={styles.card} testID={`form-card-${form.id}`}>
            <View style={styles.row}>
                <View style={styles.icon}><Unicon name="document-text-outline" size={20} colorVariant="forms" /></View>
                <TouchableOpacity
                    style={styles.copy}
                    onPress={onPress}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel={`Open ${form.title}`}
                    testID={`form-open-${form.id}`}
                >
                    <Text numberOfLines={1} style={styles.title}>{form.title}</Text>
                    {!!(subtitle || form.description) && <Text numberOfLines={2} style={styles.description}>{subtitle || form.description}</Text>}
                </TouchableOpacity>
                {menuOptions.length > 0 && anchor(guideTargets.entries, <DropdownMenu testID={`form-menu-${form.id}`} options={menuOptions} />)}
            </View>
            <View style={styles.footer}>
                <View style={styles.meta}>
                    <View style={styles.chip(status.tone)} testID={`form-status-${form.status}`}>
                        <Text style={styles.chipText(status.tone)}>{status.label}</Text>
                    </View>
                    <View style={styles.neutralChip}>
                        <Unicon name={form.isPublic ? 'globe-outline' : 'lock-closed-outline'} size={12} colorVariant="textSecondary" />
                        <Text style={styles.neutralText}>{formVisibilityLabel(form)}</Text>
                    </View>
                    {showEntryCount && <Text style={styles.entryCount}>{form.entryCount || 0} entries</Text>}
                </View>
                {!!onAction && anchor(guideTargets.fill,
                    <SolidButton
                        text={actionLabel}
                        onPress={onAction}
                        type="forms"
                        size="sm"
                        testID={`form-action-${form.id}`}
                        accessibilityLabel={`${actionLabel}: ${form.title}`}
                    />
                )}
            </View>
            {!!myEntry && anchor(guideTargets.myEntry,
                <TouchableOpacity
                    testID={`form-my-entry-${form.id}`}
                    accessibilityRole="button"
                    accessibilityLabel={`View your submitted entry for ${form.title}`}
                    activeOpacity={0.7}
                    onPress={() => onMyEntryPress?.(myEntry)}
                    style={styles.myEntry}
                >
                    <Unicon name="mail-open-outline" size={15} colorVariant="forms" />
                    <Text numberOfLines={1} style={styles.myEntryText}>
                        Your entry{myEntry.createdAt ? ` · Submitted ${new Date(myEntry.createdAt).toLocaleDateString()}` : ''}
                    </Text>
                    <Unicon name="chevron-forward" size={15} colorVariant="textSecondary" />
                </TouchableOpacity>
            )}
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    card: { backgroundColor: theme.colors.surface, borderRadius: 14, padding: 14, gap: 11, boxShadow: theme.shadows.card },
    row: { flexDirection: 'row', alignItems: 'center', gap: 11 },
    icon: { width: 40, height: 40, borderRadius: 10, backgroundColor: theme.colors.formsContainer, alignItems: 'center', justifyContent: 'center' },
    copy: { flex: 1, minWidth: 0 },
    title: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 15 },
    description: { color: theme.colors.textSecondary, fontSize: 12, lineHeight: 17, marginTop: 2 },
    footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 9 },
    meta: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
    chip: (tone) => ({ backgroundColor: theme.colors[`${tone}Container`] || theme.colors.surfaceVariant, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 }),
    chipText: (tone) => ({ color: theme.colors[tone] || theme.colors.textSecondary, fontFamily: theme.fonts.bold, fontSize: 11 }),
    neutralChip: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: theme.colors.border, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
    neutralText: { color: theme.colors.textSecondary, fontFamily: theme.fonts.bold, fontSize: 11 },
    entryCount: { color: theme.colors.textSecondary, fontSize: 11 },
    myEntry: { flexDirection: 'row', alignItems: 'center', gap: 8, borderTopWidth: 1, borderTopColor: theme.colors.border, paddingTop: 10 },
    myEntryText: { flex: 1, color: theme.colors.textSecondary, fontSize: 12 },
}));
